import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Settings, Type, Tag, FileText, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CONFIG_GROUPS = [
  {
    label: 'Landing Page Intro / Briefing',
    icon: FileText,
    keys: ['landing_intro_heading', 'landing_intro_text', 'landing_intro_cta'],
  },
  {
    label: 'Page Titles',
    icon: Type,
    keys: ['home_hero_title', 'home_hero_subtitle', 'treasure_map_title', 'mind_map_title', 'library_title', 'git_title', 'escort_guard_title'],
  },
  {
    label: 'Subscription Tier Names',
    icon: Layers,
    keys: ['tier_seeker_name', 'tier_berean_name', 'tier_scholar_name', 'tier_church_name'],
  },
  {
    label: 'Tier Taglines',
    icon: Tag,
    keys: ['seeker_tagline', 'berean_tagline', 'scholar_tagline', 'church_tagline'],
  },
  {
    label: 'Product Descriptions',
    icon: FileText,
    keys: ['ebook_description', 'appendices_description', 'curriculum_description', 'print_description'],
  },
];

const FRIENDLY_NAMES = {
  landing_intro_heading: 'Intro Heading (e.g. "Welcome, Seeker")',
  landing_intro_text: 'Intro Briefing Text (paragraph before the map)',
  landing_intro_cta: 'Call-to-Action Button Text (e.g. "Begin the Journey")',
  tier_seeker_name: 'Seeker Tier Name',
  tier_berean_name: 'Berean Tier Name',
  tier_scholar_name: 'Scholar Tier Name',
  tier_church_name: 'Church/Pastor Tier Name',
  seeker_tagline: 'Seeker Tagline',
  berean_tagline: 'Berean Tagline',
  scholar_tagline: 'Scholar Tagline',
  church_tagline: 'Church Tagline',
  home_hero_title: 'Home Page Title',
  home_hero_subtitle: 'Home Page Subtitle',
  treasure_map_title: 'Treasure Map Title',
  mind_map_title: 'Mind Map Title',
  library_title: 'Library Title',
  git_title: 'GIT Investigator Title',
  escort_guard_title: 'Escort Guard Title',
  ebook_description: 'eBook Description',
  appendices_description: 'Appendices Description',
  curriculum_description: 'Curriculum Description',
  print_description: 'Print Edition Description',
};

const AdminSiteConfig = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API}/api/site-config/`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config || {});
        setOriginal(data.config || {});
      }
    } catch (err) {
      toast.error('Failed to load config');
    }
    setLoading(false);
  };

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const getChangedKeys = () => {
    return Object.keys(config).filter(k => config[k] !== original[k]);
  };

  const handleSave = async () => {
    const changed = getChangedKeys();
    if (changed.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const updates = {};
      changed.forEach(k => { updates[k] = config[k]; });

      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API}/api/site-config/bulk`, {
        method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify({ updates }),
      });

      if (res.ok) {
        toast.success(`Saved ${changed.length} change(s)`);
        setOriginal({ ...config });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Error saving config');
    }
    setSaving(false);
  };

  const changedCount = getChangedKeys().length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-amber-600/50 animate-pulse">Loading site configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100" data-testid="admin-site-config">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 mb-6 transition-colors"
          data-testid="back-to-admin"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Settings className="h-6 w-6 text-amber-500" />
              Site Content Manager
            </h1>
            <p className="text-sm text-gray-500 mt-1">Edit text, tier names, and labels across the site</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={changedCount === 0 || saving}
            data-testid="save-config-btn"
            className={`${changedCount > 0 ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-gray-700'} text-white`}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : changedCount > 0 ? `Save ${changedCount} Change${changedCount > 1 ? 's' : ''}` : 'No Changes'}
          </Button>
        </div>

        <div className="space-y-6">
          {CONFIG_GROUPS.map(group => (
            <div key={group.label} className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid={`config-group-${group.label.toLowerCase().replace(/\s/g, '-')}`}>
              <h2 className="text-sm font-mono uppercase tracking-wider text-gray-400 mb-4 flex items-center gap-2">
                <group.icon className="h-4 w-4" />
                {group.label}
              </h2>
              <div className="space-y-4">
                {group.keys.map(key => {
                  const isChanged = config[key] !== original[key];
                  const isLongText = key === 'landing_intro_text' || key.includes('description') || key.includes('tagline');
                  return (
                    <div key={key} className="flex items-start gap-4">
                      <div className="w-44 shrink-0 pt-2">
                        <label className="text-sm text-gray-400">{FRIENDLY_NAMES[key] || key}</label>
                        {isChanged && <span className="ml-2 text-xs text-amber-400">modified</span>}
                      </div>
                      {isLongText ? (
                        <textarea
                          data-testid={`config-input-${key}`}
                          value={config[key] || ''}
                          onChange={e => handleChange(key, e.target.value)}
                          rows={key === 'landing_intro_text' ? 5 : 2}
                          className={`flex-1 bg-gray-800 border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-600/50 transition-colors resize-y ${
                            isChanged ? 'border-amber-600/50' : 'border-gray-700'
                          }`}
                        />
                      ) : (
                        <input
                          data-testid={`config-input-${key}`}
                          value={config[key] || ''}
                          onChange={e => handleChange(key, e.target.value)}
                          className={`flex-1 bg-gray-800 border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-600/50 transition-colors ${
                            isChanged ? 'border-amber-600/50' : 'border-gray-700'
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
          <p className="text-xs text-gray-500">
            Changes are applied site-wide after saving. Some changes (like tier names) will reflect immediately across all pages.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminSiteConfig;
